import { useEffect } from "react";
import {
  useOrdersStore,
  useInitializeOrderPermissions,
} from "./use-orders-store";
import OrderAll from "./order-all";
import { useParams } from "react-router-dom";
import { toastHandler } from "@/lib/ToastHandler";
import { initOrdersRealtime, cleanupOrdersRealtime } from "./orders-realtime";
import { useBranches } from "@/hooks/useBranches";
import { useAuth } from "@/hooks/useAuth";

// Full screen loading component for initial load
const FullScreenLoading = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      <p className="text-gray-600 text-sm">Cargando órdenes del día...</p>
    </div>
  </div>
);

export default function OrderApp() {
  // Initialize permissions
  useInitializeOrderPermissions();
  const hasInitiallyFetched = useOrdersStore(
    (state) => state.hasInitiallyFetched,
  );

  const isLoading = useOrdersStore((state) => state.isLoading);
  const fetchOrderById = useOrdersStore((state) => state.fetchOrderById);
  const { orderId } = useParams<{ orderId: string }>();
  const { fetchBranches } = useBranches();
  const {
    isLoading: authLoading,
    userDataLoading,
    isManager,
    user,
  } = useAuth();

  useEffect(() => {
    useOrdersStore.getState().fetchPaginatedOrdersCurrentDay();
    useOrdersStore.getState().fetchCurrentDayOrdersCounts();
  }, [hasInitiallyFetched]);

  // Ensure branches are fetched once when auth is ready
  useEffect(() => {
    const authReady =
      !authLoading && !userDataLoading && (!isManager || Boolean(user?.email));
    if (authReady) {
      fetchBranches().catch(() => {});
    }
  }, [authLoading, userDataLoading, isManager, user?.email, fetchBranches]);

  // Initialize realtime connection and show connection toasts
  useEffect(() => {
    initOrdersRealtime();

    const onOpen = () =>
      toastHandler({
        title: "Conectado a tiempo real",
        description: "Recibirás actualizaciones de órdenes al instante.",
        variant: "default",
      });
    const onClose = () =>
      toastHandler({
        title: "Desconectado de tiempo real",
        description: "Intentando reconectar...",
        variant: "destructive",
      });

    window.addEventListener("orders-realtime:open", onOpen as EventListener);
    window.addEventListener("orders-realtime:close", onClose as EventListener);

    return () => {
      window.removeEventListener(
        "orders-realtime:open",
        onOpen as EventListener,
      );
      window.removeEventListener(
        "orders-realtime:close",
        onClose as EventListener,
      );
      cleanupOrdersRealtime();
    };
  }, []);

  // Fetch detail when there is an orderId in the URL
  useEffect(() => {
    if (orderId) {
      const idNum = Number(orderId);
      if (!Number.isNaN(idNum)) {
        fetchOrderById(idNum);
      }
    }
  }, [orderId, fetchOrderById]);

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="min-h-screen">
      <OrderAll />
    </div>
  );
}
